import { formatDistance } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { ClipDataBuilder, ClipboardService } from '../../platform/Clipboard';
import { useLoader, useSubmitter } from '../../platform/Hook';
import { Button, Colors, Input } from '../../ui';
import { Notice } from '../../ui/Notice';
import { Position } from '../../ui/Position';
import { TextButton } from '../../ui/TextButton';
import { useResolve } from '../Container';
import { ShowIfHasFeature } from '../common/FeatureElements';
import { Copy, DisableFrame, Flexer, FlexerProps, Title } from '../dashboard/elements/LayoutElements';

const ExpireMessage = styled.span`
    font-size: .875rem;
    color: ${Colors.textLight};
`;

const flash = keyframes`
    0% {
        inset: 0;
        opacity: .5;
        border-width: .125rem;
        border-radius: .25rem;
    }
    100% {
        inset: -1rem;
        opacity: 0;
        border-width: .5rem;
        border-radius: 1rem;
    }
`;

const FlashFrame = styled(Flexer)`
    position: relative;
`;

const FlashEffect = styled.div`
    position: absolute;
    inset: 0;
    border: solid 4px ${Colors.primary};
    border-radius: .25rem;
    opacity: .5;
    animation: ${flash} .15s ease-out;
    animation-fill-mode: forwards;
    pointer-events: none;
`;

const Flash: LFC<{ ticker: number } & FlexerProps> = ({ ticker, children, ...rest }) => {
    return <FlashFrame key={ticker} {...rest}>
        {children}
        {!!ticker && <FlashEffect />}
    </FlashFrame>;
};

export const AccountLinkInviteView = ({ compact }: { compact?: boolean }) => {

    const api = useResolve(ApiClient);
    const clipboard = useResolve(ClipboardService);
    const [copied, setCopied] = useState(0);

    // Endpoint supports multiple (for legacy reasons) but UI only supports one, so bare that in mind!
    const [_loading, links, _err, { refresh }] = useLoader(async () => {
        const initial = await api.exec(v2.admin.invites.list('general'));
        if (initial.length > 0) return initial;
        await api.exec(v2.admin.invites.create({ expiry: '14d' }));
        return await api.exec(v2.admin.invites.list('general'));
    }, []);

    const current = useMemo(() => !!links ? links[0] : null, [links]);

    const [onGenerate, working] = useSubmitter(async () => {
        const existing = await api.exec(v2.admin.invites.list('general'));
        for (const e of existing) {
            await api.exec(v2.admin.invites.delete({ inviteId: e?.id }));
        }
        await api.exec(v2.admin.invites.create({ expiry: '14d' }));
        await refresh();
    }, [current, refresh]);

    const onCopyLink = useCallback(() => {
        if (!current) return;
        const clip = new ClipDataBuilder();
        clip.setText(current.url);
        clipboard.write(clip.build());
        setCopied(x => x + 1);
    }, [current]);

    return <Flexer relative flex="column" gap=".25">
        {!compact && <>
            {!!copied && <Position inset=".25" at="topRight">
                <Title size="sm" flush color={Colors.positive}>Copied!</Title>
            </Position>}
            <Copy flush bold>
                Share this link with your team:
            </Copy>
        </>}
        <Flexer flex="row">
            <Flexer relative grow margin="0 .25 0 0" background="white">
                <Flash ticker={copied}>
                    <Input type="readonly" value={(current?.url ?? '')} />
                </Flash>
            </Flexer>
            {!compact &&
                <Button size="md" variant={compact ? 'common' : 'primary'} disabled={working} onAction={onCopyLink}>
                    Copy Link
                </Button>
            }
        </Flexer>
        {!!compact && <>
            <Flexer flex="row-reverse" padding="0 .5">
                <TextButton size=".875" underline onClick={onCopyLink}>Copy Link</TextButton>
            </Flexer>
        </>}
        {!compact && <>
            {current &&
                <ExpireMessage>
                    <Flexer flex="row" gap=".25">
                        This link will expire in {formatDistance(new Date(), current.expiresAt)}.
                        <DisableFrame disabled={working}>
                            <TextButton underline onClick={onGenerate}>Generate new link</TextButton>
                        </DisableFrame>
                    </Flexer>
                </ExpireMessage>
            }
            <Flexer height="1" />
            <ShowIfHasFeature name="accessControl">
                <Notice icon="exclamation" text="People invited via link will be added as Members, which contributes to your overall billed seats." />
            </ShowIfHasFeature>
        </>}
    </Flexer>;
};


